// Fichier unique de labels FR. Toute chaîne visible par un utilisateur
// (hors pages légales) DOIT passer par ce fichier. Règle instaurée le
// 2026-08-31 (pass vocab « on parle chantier, pas plateforme »).
//
// Registres sémantiques :
//   • « artisan »       → quand on parle DE l'utilisateur qui exerce le métier
//                          (son profil, son espace, ses disponibilités).
//   • « sous-traitant » → quand on parle DE la relation contractuelle vue
//                          par le donneur d'ordre (attribution, PV, contrat).
//   • « chantier »      → remplace « besoin » côté UI (routes/API inchangées).
//   • « à jour »        → remplace « conforme » pour le statut docs. Exceptions
//                          réglementaires (Factur-X EN 16931, numérotation
//                          des factures) : les termes légaux restent en dur.
//
// Règle d'ingénierie stricte : les clés backend (rôle=prestataire,
// matching_besoins, collections Mongo, /api/prestataires, /api/besoins…) et
// les data-testid ne bougent JAMAIS. Seul l'affichage change.
//
// URL front renommées le 2026-08-31 : /app/besoins → /app/chantiers. Les
// anciennes routes redirigent en 301 client (React Router <Navigate>). Ces
// redirections sont GARDÉES INDÉFINIMENT (les e-mails déjà envoyés pointent
// dessus). L'espace /app/* est bloqué dans robots.txt : la limitation du
// 301 côté client (pas de vrai HTTP 301) n'a aucun impact SEO puisque ces
// pages ne sont jamais indexées.

export const L = {
  brand: { name: "VosCompagnons", tagline: "Une relation de savoir-faire" },

  // Vocabulaire — SEULE source de vérité pour les tokens.
  termes: {
    artisan: "artisan",
    Artisan: "Artisan",
    lArtisan: "l'artisan",
    unArtisan: "un artisan",
    artisans: "artisans",
    sousTraitant: "sous-traitant",
    unSousTraitant: "un sous-traitant",
    leSousTraitant: "le sous-traitant",
    auSousTraitant: "au sous-traitant",
    sousTraitants: "sous-traitants",
    chantier: "chantier",
    Chantier: "Chantier",
    unChantier: "un chantier",
    leChantier: "le chantier",
    ceChantier: "ce chantier",
    chantiers: "chantiers",
    Chantiers: "Chantiers",
    aJour: "à jour",
    documentsAJour: "documents à jour",
  },

  nav: {
    accueil: "Accueil", tableauDeBord: "Tableau de bord", prestataires: "Sous-traitants",
    besoins: "Chantiers", messages: "Messages", missions: "Missions", monProfil: "Mon profil",
    mesDocuments: "Mes documents", monCalendrier: "Mon calendrier", factures: "Factures", admin: "Admin",
    connexion: "Connexion", inscription: "Inscription", deconnexion: "Déconnexion",
  },
  auth: {
    email: "Adresse e-mail", password: "Mot de passe", passwordConfirm: "Confirmer le mot de passe",
    role: "Je suis",
    roleEntreprise: "Entreprise (donneur d'ordre)",
    rolePrestataire: "Artisan, auto-entrepreneur ou TPE",
    raisonSociale: "Raison sociale", siret: "SIRET (14 chiffres)", telephone: "Téléphone",
    login: "Se connecter", register: "Créer mon compte", forgot: "Mot de passe oublié ?",
    reset: "Réinitialiser le mot de passe", back: "Retour",
    loginTitle: "Connexion à VosCompagnons", registerTitle: "Inscription B2B",
    forgotTitle: "Réinitialisation du mot de passe",
    verifiedInfo: "Votre compte est actif. Vous pouvez vous connecter.",
    hasAccount: "Déjà inscrit ?", noAccount: "Pas encore de compte ?",
  },
  compliance: {
    // Statuts canoniques (Chantier 2 — 2026-09) — le mot « conforme » disparaît.
    // Deux libellés côté utilisateur (artisan) : ``a_controler`` s'affiche
    // « En cours de vérification » — un artisan ne sait pas ce qu'est un
    // statut « à contrôler ». Côté admin (file de traitement), on garde
    // « À contrôler » qui décrit son action.
    verifie: "À jour",
    a_controler: "En cours de vérification",   // libellé artisan (utilisateur)
    a_controler_admin: "À contrôler",          // libellé admin (traitement)
    expire_bientot: "Expire bientôt",
    expire: "Expiré",
    non_fourni: "Non fourni",
    refuse: "Refusé",
    // Alias legacy — supportés le temps que tout le back finisse la
    // migration (le backend renvoie déjà les statuts canoniques).
    valide: "À jour",
    expirant: "Expire bientôt",
    a_verifier: "En cours de vérification",
    incomplet: "Non fourni",
    colonneTitre: "Documents",
    slaFallback: "sous quelques jours ouvrés",   // repli si /documents/config indisponible
  },
  docTypes: {
    kbis: "Extrait KBIS", urssaf: "Attestation URSSAF", rc_pro: "Assurance RC Pro",
    decennale: "Assurance décennale", attestation_fiscale: "Attestation fiscale",
  },
  missionStatus: {
    proposee: "Proposée", acceptee: "Acceptée", en_cours: "En cours",
    terminee: "Terminée", annulee: "Annulée",
  },
  common: {
    save: "Enregistrer", cancel: "Annuler", delete: "Supprimer", edit: "Modifier",
    search: "Rechercher", filter: "Filtrer", loading: "Chargement…", none: "Aucun résultat.",
    yes: "Oui", no: "Non", back: "Retour", submit: "Envoyer", view: "Consulter",
    valider: "Valider", refuser: "Refuser", suspendre: "Suspendre", reactiver: "Réactiver",
  },
  legal: {
    mentions: "Mentions légales", cgu: "CGU", cgv: "CGV",
    confidentialite: "Confidentialité (RGPD)", responsabilite: "Responsabilité RGPD", cookies: "Cookies",
  },

  // Phrases plus longues. Regroupées par page/contexte, mais partagent
  // toutes les tokens ci-dessus : changer un token propagera partout.
  wording: {
    // Landing
    landingSuiviDocs: "Suivi des documents à jour",
    landingPublicationChantiers: "Publication de chantiers illimitée",

    // DashboardRouter
    espaceArtisanIndep: "Espace artisan indépendant",
    espaceDonneurOrdre: "Espace donneur d'ordre",
    dashMesChantiers: "Mes chantiers",
    dashPublierChantier: "Publier un chantier",
    dashVoirChantiersOuverts: "Voir les chantiers ouverts",
    dashRechercherSousTraitant: "Rechercher un sous-traitant",

    // Besoins (liste côté entreprise)
    besoinsListH1: "Mes chantiers publiés",
    besoinsListPublier: "Publier un chantier",

    // NouveauBesoin
    nouveauBesoinH1: "Publier un chantier",
    nouveauBesoinSubmit: "Publier le chantier",
    nouveauBesoinGeoHint: "Géocodage automatique via l'API officielle française (api-adresse.data.gouv.fr). Utilisé pour le calcul de distance avec le rayon d'intervention des artisans.",
    nouveauBesoinSurDescriptif: "Les artisans chiffreront à partir du descriptif et des pièces jointes.",
    nouveauBesoinDatesContractuelles: "Ces deux dates deviennent contractuelles à l'acceptation du sous-traitant. Toute modification ultérieure requiert l'accord des deux parties. Un rappel automatique est envoyé si la date de fin est dépassée sans déclaration d'achèvement.",
    nouveauBesoinCoherent: (total) => `Total : ${total} — cohérent avec le montant de la mission.`,

    // SearchPrestataires
    rechercheH1: "Rechercher un sous-traitant",
    rechercheRayonHint: "Rayon max (km) — vide = respecte le rayon de l'artisan",

    // BesoinDetail
    besoinDetailConfirmAttribuer: "Attribuer ce chantier à ce sous-traitant ? Les autres candidatures seront refusées.",
    besoinDetailAttribueSuccess: "Chantier attribué. Le sous-traitant retenu voit désormais les documents contractuels.",
    besoinDetailAccesRefuse: "Accès refusé : réservé au donneur d'ordre et au sous-traitant retenu pendant une mission active.",
    besoinDetailDocsPublicsHint: "Descriptif, CCTP, plans, photos existant. Visibles par tout artisan consultant ce chantier. Maximum 5 fichiers · 10 Mo par fichier.",
    besoinDetailDocsContractuelsHint: "Bon de commande, devis validé, contrat. Visibles uniquement par vous et le sous-traitant retenu après attribution.",
    besoinDetailAccesChantierHint: "Digicode, code portail, emplacement des clés, consignes d'accès. Chiffré côté serveur (Fernet). Visible uniquement par vous, l'admin, et le sous-traitant retenu pendant une mission active. Purge automatique 30 jours après la fin de mission. Chaque consultation est journalisée.",
    besoinDetailCandidatureTh: "Artisan",

    // MesMissions
    missionsPvRefuseToast: "PV refusé. Le sous-traitant peut resoumettre.",
    missionsReservesPlaceholder: "Réserves techniques, malfaçons, travaux à reprendre…",

    // PrestataireProfile
    prestaProfileConsultationInformera: "L'artisan sera informé de votre consultation",
    prestaProfileConsultationRestreinte: "L'artisan a restreint la consultation de ce document aux entreprises engagées dans une mission.",
    prestaProfileEnTantQue: "En tant qu'artisan",

    // MonProfil
    monProfilH1: "Mon profil artisan",

    // NotifPreferences
    notifNouveauxArtisans: "Nouveaux artisans disponibles",

    // AdminNotifications
    adminNotifAlerteChantiers: "Alerte nouveaux chantiers",
    adminNotifAlerteArtisans: "Alerte nouveaux artisans",
    adminNotifCorrespondances: "Correspondances par chantier",

    // MonCalendrier
    calendrierJoursNonRenseignes: "Les jours non renseignés restent visibles dans la recherche, avec la mention « à confirmer ».",

    // GeoConfirmBanner
    geoZonePreremplie: "Nous avons pré-rempli votre zone à partir de votre SIRET. Confirmez ou corrigez pour recevoir les chantiers de votre secteur.",
    geoZoneManque: "Nous n'avons pas pu déduire votre zone à partir du SIRET. Renseignez-la manuellement pour recevoir les chantiers.",

    // AssistantWidget
    assistantIntro: "Je réponds uniquement aux questions sur l'utilisation de la plateforme (créer un profil, déposer un document, publier un chantier, candidater, statuts des documents, disponibilités…).",

    // Register
    registerRoleArtisanLabel: "Artisan, auto-entrepreneur ou TPE",

    // Landing (résidus complémentaires)
    landingCarteArtisanTag: "Artisan indépendant",
    landingCarteEntreprisePublier: "Publier un chantier",
    landingHowStep1Title: "Publiez votre chantier",
    landingHowPrestataireTag: "Artisan",

    // BesoinDetail (résidu complémentaire)
    besoinDetailContractLockedNotice: "Les documents contractuels seront accessibles au sous-traitant retenu après attribution.",
  },

  // Versements (2026-10) — acompte / situations / solde / retenue.
  // Registre : côté artisan on dit « donneur d'ordre », jamais « client » ;
  // côté donneur d'ordre on dit « artisan ».
  versements: {
    titre: "Chaîne financière",
    statut: {
      declare: ["En attente de confirmation", "badge-warn"],
      en_attente_validation: ["En attente de validation — relancé", "badge-danger"],
      partiel_propose: ["Validation partielle proposée", "badge-warn"],
      valide_attente_liberation: ["Validé, en attente de libération", "badge-valid"],
      conteste: ["Contesté", "badge-danger"],
      retenue_constituee: ["Retenue constituée", "badge-muted"],
      levee_declaree: ["Levée des réserves déclarée", "badge-warn"],
    },
    attenteArtisan: "Votre donneur d'ordre n'a pas encore confirmé. Nous le relançons.",
    attenteDO: "Votre confirmation est attendue sous 48 h ouvrées. Le silence ne vaut jamais accord.",
    aucuneLiberation: "Aucun versement ne se libère sans votre confirmation explicite.",
    sequestreInfo: "La libération effective des fonds n'est pas encore branchée : le statut « validé, en attente de libération » fait foi.",
    declarerDemarrage: "Déclarer le démarrage",
    declarerSituation: "Déclarer une situation",
    demanderReception: "Demander la réception",
    leverReserves: "Déclarer les réserves levées",
    confirmerDemarrage: "Je confirme le démarrage",
    contester: "Je conteste",
    valider: "Je valide",
    validerPartiel: "Je valide partiellement",
    receptionSansReserve: "Réception sans réserve",
    receptionAvecReserves: "Réception avec réserves",
    refusReception: "Refus de réception",
    motifObligatoire: "Motif obligatoire",
    pvPdf: "PV de réception (PDF)",
  },
};

// Taxonomie métiers — SOURCE DE VÉRITÉ UNIQUE dans /app/frontend/src/constants/metiers.json.
// Ce même fichier est lu côté backend (server.py, load_taxonomy) pour valider les
// combinaisons métier / spécialité / sous-spécialité sur POST /api/besoins et
// PUT /api/prestataires/me. Ne dupliquez pas la nomenclature ailleurs — éditez
// uniquement metiers.json.
import metiersData from "./metiers.json";
export const METIERS = metiersData;

// Métiers dont la facturation est habituellement à la période (location
// mensuelle) plutôt qu'horaire/journalière. Utilisé pour afficher
// conditionnellement le champ "tarif mensuel HT" sur le profil prestataire
// et dans la recherche — sans forcer les autres métiers à le renseigner.
export const METIERS_TARIF_MENSUEL = [
  "10000 — Pose, montage et location d'échafaudages",
];
export const showsTarifMensuel = (metier) => METIERS_TARIF_MENSUEL.includes(metier || "");

export const CERTIFS = [
  "RGE (Reconnu Garant de l'Environnement)",
  "Qualibat",
  "Qualifelec",
  "Qualit'ENR",
  "Certibat",
  "Ecoartisan",
  "Handibat",
  "MASE (sécurité chantier)",
  "ISO 9001",
  "Chantier propre",
];

// Format helpers FR
export const fmtEUR = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n));
};
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
};

// Formatage du budget selon le type sélectionné
export const fmtBudget = (b) => {
  if (!b) return "—";
  const t = b.budget_type || "sur_descriptif";
  if (t === "sur_descriptif") return "Sur descriptif";
  if (t === "indicatif" && b.budget_ht) return `~ ${fmtEUR(b.budget_ht)} indicatif`;
  if (t === "montant" && b.budget_ht) return fmtEUR(b.budget_ht);
  if (t === "fourchette" && (b.budget_min || b.budget_max)) {
    return `${fmtEUR(b.budget_min)} – ${fmtEUR(b.budget_max)}`;
  }
  return "—";
};

// Formatage de la durée
export const fmtDuree = (b) => {
  if (!b) return "—";
  if (b.duree_texte) return b.duree_texte;
  if (b.duree_valeur && b.duree_unite) return `${b.duree_valeur} ${b.duree_unite}`;
  return "—";
};
